import styled, { keyframes } from "styled-components";

const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
`;

const auroraShift = keyframes`
    0%   { transform: translate(0%,   0%)   scale(1);    }
    25%  { transform: translate(-4%,  5%)   scale(1.06); }
    50%  { transform: translate(5%,  -3%)   scale(0.97); }
    75%  { transform: translate(-2%, -5%)   scale(1.04); }
    100% { transform: translate(0%,   0%)   scale(1);    }
`;

// Combined float + glow in one keyframe — eliminates the jitter from
// two unsynchronised animations running on the same element.
const floatGlow = keyframes`
    0% {
        transform: translateY(0px);
        box-shadow: 0 10px 20px rgba(0,0,0,0.15), 0 0 0 0 rgba(47,129,247,0.35);
    }
    30% {
        box-shadow: 0 10px 20px rgba(0,0,0,0.15), 0 0 0 8px rgba(47,129,247,0);
    }
    50% {
        transform: translateY(-12px);
        box-shadow: 0 20px 30px rgba(0,0,0,0.2), 0 0 0 0 rgba(47,129,247,0);
    }
    100% {
        transform: translateY(0px);
        box-shadow: 0 10px 20px rgba(0,0,0,0.15), 0 0 0 0 rgba(47,129,247,0);
    }
`;


const pulseRing = keyframes`
    0%   { transform: scale(1);   opacity: 0.7; }
    70%  { transform: scale(2.2); opacity: 0;   }
    100% { transform: scale(2.2); opacity: 0;   }
`;

const bounce = keyframes`
    0%, 20%, 50%, 80%, 100% {
        transform: translateY(0);
    }
    40% {
        transform: translateY(-10px);
    }
    60% {
        transform: translateY(-5px);
    }
`;

export const HeroSectionWrapper = styled.div`
    position: relative;
    padding-top: 80px;
    overflow: hidden;
`;

export const AuroraBg = styled.div`
    position: absolute;
    inset: -40%;
    z-index: 0;
    pointer-events: none;
    background:
        radial-gradient(ellipse 80% 60% at 15% 45%, rgba(47, 129, 247, 0.26) 0%, transparent 65%),
        radial-gradient(ellipse 60% 80% at 85% 25%, rgba(14, 165, 233, 0.2)  0%, transparent 60%),
        radial-gradient(ellipse 70% 50% at 55% 80%, rgba(99, 102, 241, 0.14) 0%, transparent 60%);
    animation: ${auroraShift} 18s ease-in-out infinite alternate;
    will-change: transform;
`;

export const HeroContainer = styled.div`
    background: ${({ theme }) => theme.card_light};
    display: flex;
    flex-direction: column;
    justify-content: center;
    position: relative;
    padding: 120px 30px 100px;
    z-index: 1;
    transition: opacity 0.5s ease-in-out;
    opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};

    @media (max-width: 960px) {
        padding: 66px 16px 80px;
    }
    @media (max-width: 640px) {
        padding: 32px 16px 60px;
    }
`;

export const HeroBg = styled.div`
    position: absolute;
    display: flex;
    justify-content: flex-end;
    top: 50%;
    left: 50%;
    width: 100%;
    height: 100%;
    max-width: 1360px;
    overflow: hidden;
    padding: 0 30px;
    -webkit-transform: translateX(-50%) translateY(-50%);
    transform: translateX(-50%) translateY(-50%);

    @media (max-width: 960px) {
        justify-content: center;
        padding: 0 0px;
    }
`;

export const HeroInnerContainer = styled.div`
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    max-width: 1100px;
    margin: 0 auto;

    @media (max-width: 960px) {
        flex-direction: column;
    }
`;

export const HeroLeftContainer = styled.div`
    width: 50%;
    order: 1;
    animation: ${fadeIn} 1s ease-in-out;
    animation-fill-mode: both;

    @media (max-width: 960px) {
        width: 100%;
        order: 2;
        margin-bottom: 30px;
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
    }
`;

export const HeroRightContainer = styled.div`
    width: 50%;
    display: flex;
    order: 2;
    justify-content: flex-end;
    animation: ${fadeIn} 1s ease-in-out 0.5s;
    animation-fill-mode: both;

    @media (max-width: 960px) {
        width: 80%;
        order: 1;
        justify-content: center;
        align-items: center;
        margin-bottom: 80px;
    }

    @media (max-width: 640px) {
        width: 100%;
        margin-bottom: 30px;
    }
`;

export const Img = styled.img`
    position: relative;
    width: 100%;
    height: 100%;
    max-width: 400px;
    max-height: 500px;
    border-radius: 50%;
    border: 4px solid ${({ theme }) => theme.primary};
    animation: ${floatGlow} 6s ease-in-out infinite;
    transition: transform 0.4s ease-in-out, box-shadow 0.4s ease-in-out, border-color 0.3s;

    &:hover {
        animation-play-state: paused;
        transform: scale(1.04);
        box-shadow: 0 20px 40px rgba(47, 129, 247, 0.4);
    }

    @media (max-width: 768px) {
        max-width: 320px;
        max-height: 320px;
    }

    @media (max-width: 640px) {
        max-width: 280px;
        max-height: 280px;
    }
`;

export const Title = styled.h1`
    font-weight: 700;
    font-size: 50px;
    color: ${({ theme }) => theme.text_primary};
    line-height: 1.3;
    margin-bottom: 16px;

    @media (max-width: 960px) {
        text-align: center;
    }

    @media (max-width: 640px) {
        font-size: 40px;
        line-height: 1.2;
        margin-bottom: 8px;
    }
`;

export const TextLoop = styled.div`
  font-weight: 600;
  font-size: 32px;
  display: flex;
  gap: 12px;
  color: ${({ theme }) => theme.text_primary};
  line-height: 1.5;
  margin-bottom: 16px;
  
  @media (max-width: 960px) {
    text-align: center;
  }
  
  @media (max-width: 640px) {
    font-size: 22px;
    line-height: 1.5;
    margin-bottom: 16px;
  }
`;

export const Span = styled.span`
  background: linear-gradient(135deg, #2F81F7, #0EA5E9);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  cursor: pointer;
  transition: opacity 0.3s ease-in-out;

  &:hover {
    opacity: 0.85;
  }
`;

export const SubTitle = styled.p`
  font-size: 20px;
  line-height: 1.6;
  margin-bottom: 42px;
  color: ${({ theme }) => theme.text_primary + 'CC'};
  max-width: 650px;

  @media (max-width: 960px) {
    text-align: center;
  }

  @media (max-width: 640px) {
    font-size: 16px;
    line-height: 1.5;
    margin-bottom: 30px;
  }
`;

export const ButtonRow = styled.div`
    display: flex;
    gap: 16px;
    align-items: center;
    flex-wrap: wrap;

    @media (max-width: 960px) {
        justify-content: center;
    }
`;

export const ResumeButton = styled.a`
    appearance: button;
    text-decoration: none;
    padding: 14px 32px;
    color: ${({ theme }) => theme.white};
    border-radius: 50px;
    cursor: pointer;
    font-size: 16px;
    font-weight: 600;
    transition: all 0.3s ease-in-out !important;
    background: linear-gradient(135deg, #2F81F7 0%, #0EA5E9 100%);
    box-shadow: 0 6px 20px rgba(47, 129, 247, 0.35);

    &:hover {
        transform: translateY(-3px) scale(1.02);
        box-shadow: 0 12px 28px rgba(47, 129, 247, 0.5);
    }

    &:active {
        transform: translateY(1px) scale(0.98);
    }

    @media (max-width: 640px) {
        padding: 12px 24px;
        font-size: 15px;
    }
`;

export const ContactButton = styled.a`
    appearance: button;
    text-decoration: none;
    padding: 14px 32px;
    color: ${({ theme }) => theme.primary};
    border-radius: 50px;
    cursor: pointer;
    font-size: 16px;
    font-weight: 600;
    transition: all 0.3s ease-in-out !important;
    background: transparent;
    border: 2px solid ${({ theme }) => theme.primary};

    &:hover {
        background: ${({ theme }) => `${theme.primary}15`};
        transform: translateY(-3px) scale(1.02);
        box-shadow: 0 8px 20px rgba(47, 129, 247, 0.2);
    }

    &:active {
        transform: translateY(1px) scale(0.98);
    }

    @media (max-width: 640px) {
        padding: 12px 24px;
        font-size: 15px;
    }
`;

export const SocialMediaIcons = styled.div`
  display: flex;
  margin-top: 2rem;
  gap: 1rem;
  
  @media (max-width: 640px) {
    justify-content: center;
  }
`;

export const SocialMediaIcon = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.text_primary};
  font-size: 1.5rem;
  transition: all 0.3s ease-in-out;
  border-radius: 50%;
  padding: 8px;
  
  &:hover {
    color: ${({ theme }) => theme.primary};
    transform: translateY(-3px);
  }
`;

export const ScrollDownIcon = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 40px;
  animation: ${bounce} 2s infinite;
  font-size: 24px;
  color: ${({ theme }) => theme.text_secondary};
  cursor: pointer;
  transition: color 0.3s ease-in-out;

  &:hover {
    color: ${({ theme }) => theme.primary};
  }
`;

/* ── Hero stats ── */

export const StatsRow = styled.div`
  display: flex;
  gap: 36px;
  margin-top: 36px;
  flex-wrap: wrap;

  @media (max-width: 960px) {
    justify-content: center;
    gap: 28px;
  }

  @media (max-width: 480px) {
    gap: 20px;
  }
`;

export const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  position: relative;

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    right: -18px;
    top: 8px;
    height: 32px;
    width: 1px;
    background: ${({ theme }) => `${theme.text_primary}20`};

    @media (max-width: 960px) {
      display: none;
    }
  }

  @media (max-width: 960px) {
    align-items: center;
  }
`;

export const StatNumber = styled.div`
  font-size: 38px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -1.5px;
  background: linear-gradient(135deg, #2F81F7, #0EA5E9);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

export const StatLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.text_secondary};
  text-transform: uppercase;
  letter-spacing: 0.8px;
`;

export const AvailableBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  border-radius: 50px;
  background: rgba(34, 197, 94, 0.12);
  border: 1px solid rgba(34, 197, 94, 0.3);
  font-size: 13px;
  font-weight: 600;
  color: #22c55e;
  letter-spacing: 0.3px;
  margin-bottom: 20px;
  width: fit-content;

  @media (max-width: 960px) {
    align-self: center;
  }
`;

export const PulseDot = styled.span`
  position: relative;
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
  flex-shrink: 0;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: #22c55e;
    animation: ${pulseRing} 1.8s ease-out infinite;
  }
`;